import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Loader2, Plus, Minus, ClipboardCheck, AlertTriangle } from 'lucide-react';
import { ADJUSTMENT_REASONS } from '@/lib/stockControl';

const TYPES = [
  { key: 'increment', label: 'Add Stock', icon: Plus },
  { key: 'decrement', label: 'Remove Stock', icon: Minus },
  { key: 'recount', label: 'Recount / Set', icon: ClipboardCheck },
];

const QTY_LABEL = {
  increment: 'Quantity to add',
  decrement: 'Quantity to remove',
  recount: 'Counted quantity',
};

// Applies one stock movement to Square, then reports the result back to the
// page so the table and audit log update in place.
export default function StockAdjustModal({ row, onClose, onSaved }) {
  const [type, setType] = useState('increment');
  const [qty, setQty] = useState('');
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  if (!row) return null;
  const { product, onHand } = row;

  const counted = Number(qty);
  const valid = qty !== '' && Number.isFinite(counted) && (type === 'recount' ? counted >= 0 : counted > 0);
  const projected = !valid ? null
    : type === 'increment' ? onHand + counted
    : type === 'decrement' ? onHand - counted
    : counted;
  const delta = projected == null ? 0 : projected - onHand;

  const submit = async () => {
    if (!valid || !reason) return;
    setSaving(true);
    setError('');
    try {
      const res = await base44.functions.invoke('stockControl', {
        action: 'adjust',
        product_id: product.id,
        adjustment_type: type,
        quantity: counted,
        reason,
        note,
      });
      if (!res?.data?.success) throw new Error(res?.data?.error || 'Adjustment failed');
      onSaved(res.data);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Adjustment failed');
    }
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-white text-base font-black uppercase tracking-wider">
            Adjust Stock
          </DialogTitle>
          <DialogDescription className="text-white/40 text-xs">
            {product.name}
            {product.variation_name ? ` — ${product.variation_name}` : ''}
            {product.sku ? ` · ${product.sku}` : ''}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 pt-2">
          <div className="flex items-center justify-between border border-white/10 bg-[#0d0d0f] px-4 py-3">
            <span className="text-white/40 text-[10px] font-bold tracking-widest">CURRENT SQUARE LEVEL</span>
            <span className="text-white font-black text-lg">{onHand}</span>
          </div>

          <div>
            <p className="text-white/40 text-[10px] font-bold tracking-widest mb-2">ADJUSTMENT TYPE</p>
            <div className="grid grid-cols-3 gap-2">
              {TYPES.map((t) => {
                const Icon = t.icon;
                const active = type === t.key;
                return (
                  <button
                    key={t.key}
                    onClick={() => setType(t.key)}
                    className={`flex flex-col items-center gap-1.5 px-3 py-3 border text-[10px] font-bold tracking-widest transition-colors ${
                      active ? 'border-primary bg-primary/10 text-primary' : 'border-white/10 text-white/40 hover:text-white hover:border-white/25'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {t.label.toUpperCase()}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-white/40 text-[10px] font-bold tracking-widest mb-1 block">
                {QTY_LABEL[type].toUpperCase()}
              </label>
              <input
                type="number"
                min="0"
                value={qty}
                onChange={(e) => setQty(e.target.value)}
                className="seo-input"
                autoFocus
              />
            </div>
            <div>
              <label className="text-white/40 text-[10px] font-bold tracking-widest mb-1 block">REASON *</label>
              <select value={reason} onChange={(e) => setReason(e.target.value)} className="seo-input">
                <option value="">— Select reason —</option>
                {ADJUSTMENT_REASONS.map((r) => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
            </div>
          </div>

          {projected != null && delta !== 0 && (
            <div className="flex items-center gap-3 border border-white/10 bg-[#0d0d0f] px-4 py-3">
              <span className="text-white/40 text-[10px] font-bold tracking-widest">NEW SQUARE LEVEL</span>
              <span className="text-white font-black">{onHand}</span>
              <span className="text-white/30">→</span>
              <span className={`font-black ${projected < 0 ? 'text-red-400' : projected === 0 ? 'text-amber-400' : 'text-green-400'}`}>
                {projected}
              </span>
              <span className={`ml-auto text-[10px] font-bold tracking-widest ${delta < 0 ? 'text-red-400' : 'text-green-400'}`}>
                {delta > 0 ? `+${delta}` : delta}
              </span>
            </div>
          )}

          {projected != null && projected < 0 && (
            <p className="flex items-center gap-2 text-red-400 text-[11px]">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
              This takes the Square level below zero — check the count before continuing.
            </p>
          )}

          <div>
            <label className="text-white/40 text-[10px] font-bold tracking-widest mb-1 block">NOTE (OPTIONAL)</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              className="seo-input resize-y"
              placeholder="Anything worth recording against this movement"
            />
          </div>

          {error && (
            <p className="flex items-start gap-2 text-red-400 text-[11px] border border-red-400/30 bg-red-400/5 px-3 py-2">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" /> {error}
            </p>
          )}

          <div className="flex gap-3">
            <button
              onClick={submit}
              disabled={!valid || !reason || saving}
              className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 disabled:opacity-40 text-white font-bold text-xs tracking-widest px-5 py-3 transition-colors"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <ClipboardCheck className="w-4 h-4" />}
              APPLY TO SQUARE
            </button>
            <button
              onClick={onClose}
              disabled={saving}
              className="border border-white/10 hover:border-white/25 text-white/50 hover:text-white font-bold text-xs tracking-widest px-5 py-3 transition-colors"
            >
              CANCEL
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}